
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { SolicitacaoFormData } from '@/types/solicitacao';
import { useToast } from '@/hooks/use-toast';
import { useSLACalculation } from './useSLACalculation';

export const useSolicitacaoUpdate = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { calculateAndSetSLADeadlines } = useSLACalculation();

  // Função para criar logs de alteração
  const createAuditLog = async (
    requisicaoId: string, 
    acao: string, 
    tipo: string, 
    userId: string
  ) => {
    try {
      const { data, error } = await supabase
        .from('requisicao_logs')
        .insert({
          requisicao_id: requisicaoId,
          acao,
          tipo,
          usuario_id: userId,
        })
        .select();
      
      if (error) {
        console.error('Erro ao criar log:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error creating audit log:', error);
    }
  };

  // Função para comparar valores e gerar logs
  const generateAuditLogs = async (
    requisicaoId: string,
    oldData: any,
    newData: any,
    userId: string
  ) => {
    const logs: Array<{ acao: string; tipo: string }> = [];

    // Verificar alterações nos campos principais
    if (oldData.status !== newData.status) {
      logs.push({
        acao: `Status alterado de "${oldData.status}" para "${newData.status}"`,
        tipo: 'status'
      });
    }

    if (oldData.data_limite_resposta !== newData.data_limite_resposta) {
      logs.push({
        acao: `Data limite resposta alterada de "${oldData.data_limite_resposta}" para "${newData.data_limite_resposta}"`,
        tipo: 'data_limite_resposta'
      });
    }

    if (oldData.data_limite_resolucao !== newData.data_limite_resolucao) {
      logs.push({
        acao: `Data limite resolução alterada de "${oldData.data_limite_resolucao}" para "${newData.data_limite_resolucao}"`,
        tipo: 'data_limite_resolucao'
      });
    }

    if (oldData.prioridade !== newData.prioridade) {
      logs.push({
        acao: `Prioridade alterada de "${oldData.prioridade}" para "${newData.prioridade}"`,
        tipo: 'prioridade'
      });
    }

    if (oldData.atendente_id !== newData.atendente_id) {
      logs.push({
        acao: `Atendente alterado`,
        tipo: 'atendente_id'
      });
    }

    if (oldData.grupo_responsavel_id !== newData.grupo_responsavel_id) {
      logs.push({
        acao: `Grupo responsável alterado`,
        tipo: 'grupo_responsavel_id'
      });
    }

    if (oldData.notas_internas !== newData.notas_internas) {
      logs.push({
        acao: `Notas internas alteradas`,
        tipo: 'notas_internas'
      });
    }

    // Criar todos os logs
    for (const log of logs) {
      await createAuditLog(requisicaoId, log.acao, log.tipo, userId);
    }
  };

  // Enviar pesquisa de satisfação ao fechar ticket
  const enviarPesquisaSatisfacao = async (solicitacao: any, userId: string) => {
    try {
      if (!solicitacao.client_id || !solicitacao.solicitante_id) return;

      // Buscar formulários de pesquisa associados ao cliente via formulario_clientes
      const { data: fcMatches } = await supabase
        .from('formulario_clientes')
        .select('formulario_id')
        .eq('client_id', solicitacao.client_id);

      if (!fcMatches || fcMatches.length === 0) return;

      const { data: formularios, error: formError } = await supabase
        .from('formularios')
        .select('id')
        .in('id', fcMatches.map((m: any) => m.formulario_id))
        .eq('pesquisa_satisfacao', true)
        .eq('envio_automatico_fechamento', true)
        .eq('status', 'ativo')
        .limit(1);

      if (formError || !formularios || formularios.length === 0) return;

      const formularioId = formularios[0].id;

      // Verificar se já existe envio para este ticket
      const { data: existente } = await supabase
        .from('pesquisa_satisfacao_envios')
        .select('id')
        .eq('solicitacao_id', solicitacao.id)
        .eq('formulario_id', formularioId)
        .maybeSingle();

      if (existente) return;

      // Criar registro de envio
      await supabase
        .from('pesquisa_satisfacao_envios')
        .insert({
          solicitacao_id: solicitacao.id,
          formulario_id: formularioId,
          respondido_por: solicitacao.solicitante_id,
          status: 'pendente',
          metadata: {
            solicitacao_numero: solicitacao.numero,
            solicitacao_titulo: solicitacao.titulo,
          },
        } as any);

      // Criar distribuição do formulário para o solicitante
      const { data: distribuicao } = await supabase
        .from('formulario_distribuicoes')
        .insert({
          formulario_id: formularioId,
          tipo_destinatario: 'usuario',
          destinatario_id: solicitacao.solicitante_id,
          enviado_por: userId,
          mensagem_personalizada: `Pesquisa de satisfação referente ao ticket ${solicitacao.numero || ''} - ${solicitacao.titulo || ''}`,
          obrigatorio: false,
        } as any)
        .select()
        .single();

      // Criar notificação para o solicitante ver no portal
      if (distribuicao) {
        await supabase
          .from('formulario_notificacoes')
          .insert({
            distribuicao_id: distribuicao.id,
            formulario_id: formularioId,
            usuario_id: solicitacao.solicitante_id,
          } as any);
      }

    } catch (error) {
      console.error('Erro ao enviar pesquisa de satisfação:', error);
    }
  };

  const updateSolicitacao = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<SolicitacaoFormData> }) => {
      console.log('Updating solicitação:', id, data);
      
      // Obter dados atuais para comparação
      const { data: currentData, error: currentError } = await supabase
        .from('solicitacoes')
        .select('*')
        .eq('id', id)
        .single();

      if (currentError) {
        throw currentError;
      }

      // Obter usuário atual
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Usuário não autenticado');
      }
      
      // Preservar prazos de SLA existentes - não recalcular
      let slaDeadlines = {
        data_limite_resposta: currentData.data_limite_resposta,
        data_limite_resolucao: currentData.data_limite_resolucao,
      };

      // Só recalcular SLA se categoria ou grupo mudaram E não havia SLA antes
      const categoriaChanged = data.categoria_id && data.categoria_id !== currentData.categoria_id;
      const grupoChanged = data.grupo_responsavel_id && data.grupo_responsavel_id !== currentData.grupo_responsavel_id;
      const noSLABefore = !currentData.data_limite_resposta && !currentData.data_limite_resolucao;

      if ((categoriaChanged || grupoChanged) && noSLABefore) {
        const calculatedDeadlines = await calculateAndSetSLADeadlines(
          data.categoria_id || currentData.categoria_id,
          data.grupo_responsavel_id || currentData.grupo_responsavel_id,
          currentData.data_abertura
        );
        
        if (calculatedDeadlines.data_limite_resposta) {
          slaDeadlines.data_limite_resposta = calculatedDeadlines.data_limite_resposta;
        }
        if (calculatedDeadlines.data_limite_resolucao) {
          slaDeadlines.data_limite_resolucao = calculatedDeadlines.data_limite_resolucao;
        }
      }

      // Gerenciar datas de resolução/fechamento baseado no status
      const statusChanged = data.status && data.status !== currentData.status;
      let data_resolucao = currentData.data_resolucao;
      let data_fechamento = currentData.data_fechamento;

      if (statusChanged) {
        const novoStatus = data.status;
        const now = new Date().toISOString();

        // Quando muda para 'resolvida', preencher data_resolucao
        if (novoStatus === 'resolvida' && !currentData.data_resolucao) {
          data_resolucao = now;
        }

        // Quando muda para 'fechada', preencher data_fechamento
        if (novoStatus === 'fechada' && !currentData.data_fechamento) {
          data_fechamento = now;
          // Se não tinha data_resolucao, usar a mesma data
          if (!currentData.data_resolucao) {
            data_resolucao = now;
          }
        }

        // Se reabrir o ticket (voltar para status não-final), limpar as datas
        if (novoStatus !== 'resolvida' && novoStatus !== 'fechada') {
          // Manter as datas históricas - não limpar para preservar rastreabilidade
        }
      }
      
      // Preparar dados para atualização
      const updateData = {
        titulo: data.titulo,
        descricao: data.descricao,
        tipo: data.tipo,
        categoria_id: data.categoria_id || null,
        sla_id: data.sla_id || null,
        urgencia: data.urgencia,
        impacto: data.impacto,
        prioridade: data.prioridade,
        status: data.status,
        solicitante_id: data.solicitante_id || null,
        client_id: data.client_id || null,
        grupo_responsavel_id: data.grupo_responsavel_id || null,
        atendente_id: data.atendente_id || null,
        canal_origem: data.canal_origem,
        data_limite_resposta: slaDeadlines.data_limite_resposta || null,
        data_limite_resolucao: slaDeadlines.data_limite_resolucao || null,
        data_resolucao: data_resolucao || null,
        data_fechamento: data_fechamento || null,
        origem_id: data.origem_id || null,
        ativos_envolvidos: data.ativos_envolvidos || null,
        notas_internas: data.notas_internas || null,
        tags: data.tags || null,
      };

      const { data: updatedData, error } = await supabase
        .from('solicitacoes')
        .update(updateData as any)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating solicitação:', error);
        throw error;
      }

      // Gerar logs de auditoria após atualização bem-sucedida
      await generateAuditLogs(id, currentData, updatedData, user.id);

      // Disparar pesquisa de satisfação se ticket foi fechado
      if (statusChanged && data.status === 'fechada') {
        await enviarPesquisaSatisfacao(currentData, user.id);
      }

      return updatedData;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['solicitacoes'] });
      toast({
        title: "Sucesso",
        description: "Solicitação atualizada com sucesso!",
      });
    },
    onError: (error: any) => {
      console.error('Error updating solicitação:', error);
      console.log('Error details:', {
        code: error?.code,
        message: error?.message,
        status: error?.status,
        details: error?.details
      });
      
      // Check for different types of permission errors
      const isPermissionError = error?.code === '42501' || 
                               error?.code === 42501 ||
                               error?.message?.includes('row-level security policy') ||
                               error?.message?.includes('Forbidden') ||
                               error?.status === 403 ||
                               (typeof error === 'object' && error !== null && 
                                (String(error).includes('42501') || String(error).includes('row-level security')));
      
      toast({
        title: "Erro de Permissão",
        description: isPermissionError 
          ? "Você não tem permissão para editar esta solicitação porque não faz parte do grupo responsável pelo caso."
          : `Erro ao atualizar solicitação: ${error?.message || 'Erro desconhecido'}`,
        variant: "destructive",
      });
    },
  });

  return updateSolicitacao;
};
